import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { Music } from 'lucide-react';
import { applyBardicInspirationUpdate, getBardicInspirationProfile, hasBardFeature } from '../../../shared/rules/bard.mjs';
import { reactionSlotOptions } from '../utils/reactionSlotOptions';
import apiFetch from '../../../utils/apiFetch';
import { notify } from '../../../utils/notification';
import './RollRequests.scss';

const newId = () => window.crypto?.randomUUID?.() || `bardic-inspiration:${Date.now()}:${Math.random().toString(36).slice(2)}`;
const post = async (url, body) => {
  const response = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw Object.assign(new Error(result.message || 'Bardic Inspiration could not be used.'), { rejected: true });
  return result;
};
const reason = target => (target.hasDie ? 'Already has a die' : !target.inRange ? `${target.distance} ft, out of range` : target.distance === null ? 'Not on the map' : `${target.distance} ft`);

/**
 * Bardic Inspiration (2024): a tile with the uses left. It opens a list of allies; giving one a die is a Bonus Action. The
 * server checks range and spends the use and the action; a saved identity makes a retry after a lost response replay
 * instead of spending twice. From Bard 5 the same window trades a spell slot for a use (Font of Inspiration, no action).
 */
export default function BardicInspirationControls({ form, characterId, campaignId, combatState, isActiveTurn, charismaModifier = 0, onCharacterChange, onCombatStateReconciled }) {
  const apiId = String(form?._id || characterId), selfId = String(characterId);
  const storageKey = `bard:inspiration:${campaignId || 'none'}:${apiId}`;
  const [pending, setPending] = useState(null), [targets, setTargets] = useState(null), [busy, setBusy] = useState(false);
  const [slotKey, setSlotKey] = useState('');
  const current = useRef(null), lock = useRef(false);
  const save = value => {
    current.current = value; setPending(value);
    try { if (value) sessionStorage.setItem(storageKey, JSON.stringify(value)); else sessionStorage.removeItem(storageKey); } catch { /* Recovery storage is optional. */ }
  };
  const loadTargets = async () => {
    try {
      const response = await apiFetch(`/characters/${encodeURIComponent(apiId)}/bard/inspiration-targets`, { cache: 'no-store' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'The party could not be loaded.');
      setTargets(result.targets || []);
    } catch (error) { setTargets([]); save({ ...current.current, error: error.message }); }
  };
  useEffect(() => {
    current.current = null; setPending(null);
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (saved?.eventId) { current.current = saved; setPending(saved); loadTargets(); }
    } catch { sessionStorage.removeItem(storageKey); }
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const profile = getBardicInspirationProfile(form, charismaModifier);
  const inCombat = Number.isInteger(combatState?.activeTurn) && Boolean(combatState?.participants?.[combatState.activeTurn]);
  const ledger = combatState?.turnActionLedger?.combatantId === selfId && combatState.turnActionLedger.turnSequence === combatState.turnSequence ? combatState.turnActionLedger : null;
  const bonusReady = (!inCombat || isActiveTurn) && !ledger?.bonusActionSpent;
  const canGive = profile.remaining > 0 && bonusReady;
  const slots = hasBardFeature(form, 'fontOfInspiration') && profile.spent > 0 ? reactionSlotOptions(form) : [];
  const key = option => `${option.pool}:${option.slotLevel}`;
  const slot = slots.find(option => key(option) === slotKey) || slots[0] || null;

  const open = () => {
    if (current.current) return;
    setTargets(null);
    save({ eventId: newId(), turnSequence: inCombat ? combatState.turnSequence : null, targetId: '', phase: 'choose' });
    loadTargets();
  };
  const give = async () => {
    if (lock.current || !current.current?.targetId) return;
    lock.current = true; setBusy(true);
    const selected = current.current;
    try {
      save({ ...selected, phase: 'saving', error: null });
      const result = await post(`/characters/${encodeURIComponent(apiId)}/bard/inspiration`, { eventId: selected.eventId, targetId: selected.targetId, turnSequence: selected.turnSequence });
      onCharacterChange?.(previous => applyBardicInspirationUpdate(previous, result.inspiration));
      if (result.combat) onCombatStateReconciled?.(result.combat);
      notify(`Bardic Inspiration: ${result.targetName} has your d${result.sides}.`, 'success');
      save(null);
    } catch (error) {
      // A refused request committed nothing; an unanswered one keeps its identity for a retry.
      save({ ...selected, phase: error.rejected ? 'choose' : 'retry', error: error.message });
      if (error.rejected) loadTargets();
    } finally { lock.current = false; setBusy(false); }
  };
  const font = async () => {
    if (lock.current || !slot) return;
    lock.current = true; setBusy(true);
    const selected = current.current;
    try {
      save({ ...selected, error: null });
      const result = await post(`/characters/${encodeURIComponent(apiId)}/bard/font-of-inspiration`, { eventId: newId(), pool: slot.pool, slotLevel: slot.slotLevel });
      onCharacterChange?.(previous => ({ ...applyBardicInspirationUpdate(previous, result.inspiration), ...(result.spellcastingState ? { spellcastingState: result.spellcastingState } : {}),
        ...(result.spellSlotProfile ? { spellSlotProfile: result.spellSlotProfile } : {}) }));
      notify(`Font of Inspiration: 1 use back for a level ${slot.slotLevel} spell slot.`, 'success');
    } catch (error) { save({ ...selected, error: error.message }); }
    finally { lock.current = false; setBusy(false); }
  };

  if (!profile.maximum) return null;
  const saving = busy || ['saving', 'retry'].includes(pending?.phase);
  const chosen = (targets || []).find(target => target.id === pending?.targetId);
  return <>
    <button type="button" data-action-tile="bardic-inspiration" className={`combat-hud-resource-tile combat-hud-feature-action-tile${canGive ? '' : ' combat-hud-resource-tile--dormant'}`} style={{ '--hud-resource-accent': '#d7b46a' }}
      title="Bardic Inspiration · Bonus Action" aria-label={`Bardic Inspiration: ${profile.remaining} of ${profile.maximum} uses, d${profile.sides}`}
      disabled={busy || Boolean(pending) || (!canGive && !slots.length)} onClick={open}>
      <span className="combat-hud-resource-tile__icon" aria-hidden="true"><Music size={20}/></span>
      <span className="combat-hud-resource-tile__name">Bardic Inspiration</span>
      <span className="combat-hud-resource-tile__value">{profile.remaining}/{profile.maximum} · d{profile.sides}</span>
    </button>
    {pending && <Modal show centered backdrop="static" keyboard={false} className="dnd-modal modern-modal roll-request-prompt" aria-label="Bardic Inspiration">
      <Modal.Body>
        <div className="roll-request__row">
          <span className="roll-request__muted"><Music size={14} aria-hidden="true"/> Bardic Inspiration · Bonus Action</span>
          <span className="roll-request__pill">{profile.remaining} of {profile.maximum} left</span>
        </div>
        <div className="roll-request__title">Who do you inspire with a d{profile.sides}?</div>
        {targets === null ? <div className="roll-request__muted">Finding your allies…</div>
          : !targets.length ? <div className="roll-request__muted">No other characters are in this campaign.</div>
            : <div role="radiogroup" aria-label="Creature to inspire">{targets.map(target => <Button key={target.id} size="sm" variant={pending.targetId === target.id ? 'warning' : 'outline-light'} role="radio" aria-checked={pending.targetId === target.id}
              className="d-flex justify-content-between w-100 mt-1" disabled={saving || target.hasDie || !target.inRange} onClick={() => save({ ...current.current, targetId: target.id, error: null })}>
              <span>{target.name}</span><span className="small">{reason(target)}</span>
            </Button>)}</div>}
        {!canGive && <div className="roll-request__muted mt-2">{profile.remaining ? (inCombat && !isActiveTurn ? 'Wait for your turn to give a die.' : 'Your Bonus Action is spent this turn.') : 'You have no uses left to give.'}</div>}
        {pending.error && <Alert variant="danger" className="mt-2 mb-0 py-1 small">{pending.error}</Alert>}
        <div className="roll-request__row mt-2">
          <Button size="sm" variant="outline-light" disabled={saving} onClick={() => save(null)}>Cancel</Button>
          {pending.phase === 'retry' ? <Button size="sm" disabled={busy} onClick={give}>Retry</Button>
            : <Button size="sm" disabled={saving || !canGive || !chosen} onClick={give}>{chosen ? `Give d${profile.sides} to ${chosen.name}` : `Give d${profile.sides}`}</Button>}
        </div>
        {slots.length > 0 && <div className="mt-3">
          <div className="roll-request__muted">Font of Inspiration: no action needed</div>
          {slots.length > 1 && <select className="form-select form-select-sm mt-1" aria-label="Spell slot to spend" value={key(slot)} disabled={saving} onChange={event => setSlotKey(event.target.value)}>
            {slots.map(option => <option key={key(option)} value={key(option)}>{option.label}</option>)}
          </select>}
          <Button size="sm" variant="outline-light" className="w-100 mt-1" disabled={saving} onClick={font}>Spend a {slots.length > 1 ? 'spell slot' : slot.label.toLowerCase()} for 1 use</Button>
        </div>}
      </Modal.Body>
    </Modal>}
  </>;
}
